<template>
  <single-metric-card
    :title="$t('goalInsightsHealth.overview.propertiesRequired')"
    :metric="hasInvalid ? counter : null"
    :loading="loading"
    :clickable="counter > 0"
    @click="handler"
  />
</template>
<script>
import SingleMetricCard from '@/components/dashboard/SingleMetricCard.vue';
import useDebounce from 'shared/composables/debounce';
import useGoalTypeProperty from '@/composables/customize-page/goal-type-property';
import useProperties from '@/composables/property/property';
import usePublishedAtFilter from '@/composables/goal/published-at-filter';
import useSubscription from '@/composables/subscription/subscription';
import { AND } from 'shared/api/query/filter';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import {
  allGoalTypesPropertiesRequiredFilter,
} from '@/lib/filter/goal/properties-required-filter';
import { applyBaseFilter } from '@/lib/filter/base-filter';
import { computed } from 'vue';
import { dogma } from '@/api';
import { hasRuleInvalidProperties, queryOverviewPropertiesRequired } from '@/api/query/goal-insights-health';

export default {
  name: 'OverviewPropertiesRequiredCard',
  props: {
    selectedCycles: {
      type: Array,
      required: true,
    },
    viewFilter: {
      type: Object,
      required: true,
    },
    goalTypes: {
      type: Array,
      required: true,
    },
    goalIdsToObserve: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['show-goal-list'],
  setup(props) {
    const { debounce } = useDebounce();
    const { subscribe } = useSubscription();
    const { properties } = useProperties();

    const goalTypesWithRequiredProperties = computed(() => props.goalTypes.map((goalType) => ({
      ...goalType,
      requiredProperties: goalType.requiredProperties.map((rp) => properties.value.find((prop) => prop.uid === rp.uid)),
    })));

    const { goalTypeProperty } = useGoalTypeProperty();
    const { publishedAtFilter } = usePublishedAtFilter();
    return {
      debounce,
      subscribe,

      goalTypesWithRequiredProperties,
      goalTypeProperty,
      publishedAtFilter,
    };
  },
  components: { SingleMetricCard },
  data() {
    return {
      series: [],
      loading: false,
    };
  },
  computed: {
    counter() {
      return this.series.length;
    },
    hasInvalid() {
      return this.goalTypes.some((goalType) => hasRuleInvalidProperties(goalType));
    },
    gqlFilterObject() {
      const handler = new GoalFilterHandler(true);
      return handler.Translate(this.viewFilter);
    },
    gqlFilter() {
      return this.gqlFilterObject.filterTree;
    },
  },
  methods: {
    handler() {
      const filteredGoalTypes = this.goalTypesWithRequiredProperties.filter((goalType) => hasRuleInvalidProperties(goalType));
      this.$emit('show-goal-list', {
        filter: {
          op: AND,
          children: [
            this.viewFilter,
            allGoalTypesPropertiesRequiredFilter(this.goalTypeProperty, filteredGoalTypes),
          ],
        },
        title: this.$t('goalInsightsHealth.overview.propertiesRequired'),
      });
    },
    fetch(wait) {
      if (!this.hasInvalid) {
        return;
      }
      const retrieve = () => {
        this.loading = true;
        dogma.query(applyBaseFilter({
          queries: [
            ...queryOverviewPropertiesRequired({
              goalCycles: this.selectedCycles,
              filter: this.gqlFilter,
              varBlocks: this.gqlFilterObject.varBlocks,
              goalTypeOptions: this.goalTypesWithRequiredProperties,
            }),
          ],
          operator: AND,
          baseFilter: this.publishedAtFilter,
        })).then((response) => {
          this.loading = false;
          if (response.status !== 200) {
            this.$showSnackbar({ color: 'error', message: this.$t('error.default') });
            return;
          }

          this.series = Object.values(response.data).flat().map((e) => e.uid);
        });
      };
      this.debounce(retrieve, wait);
    },
  },
  watch: {
    selectedCycles(val, oldVal) {
      if (JSON.stringify(val) === JSON.stringify(oldVal)) {
        return;
      }
      this.fetch(1000);
    },
    gqlFilterObject: {
      handler(val, oldVal) {
        if (JSON.stringify(val) === JSON.stringify(oldVal)) {
          return;
        }
        this.fetch(1000);
      },
      deep: true,
    },
  },
  created() {
    this.fetch(0);

    const fn = this.fetch;
    this.subscribe({
      queryFn: Promise.resolve(),
      syncFn({ isSelfEvent }) {
        const delay = isSelfEvent ? 1000 : 30000;
        fn(delay);
      },
      model: 'goal',
      includeSelfEvents: true,
    });
  },
};
</script>
